/**
 * Give kudos on the phone (docs/mobile-design.md §4.14): a bottom sheet instead of the desktop dialog. Who (people
 * or a team), a badge from a row of chips, the message, photos from the camera or library, and Post pinned at the
 * bottom with the reason it can't post yet.
 */
import { ChipRow } from './ChipRow';
import { useRef, useState } from 'react';
import clsx from 'clsx';
import { ImagePlus, X } from 'lucide-react';
import { kudosBlocker, uploadFailureText } from '@tradeoutlet/shared';
import { useGiveKudos } from '@tradeoutlet/data';
import { errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { useAuth } from '@/lib/auth';
import { useUi } from '@/lib/store';
import { uploadFile } from '@/lib/upload';
import { Spinner } from '@/components/ui';
import { AudiencePicker } from '@/features/people/AudiencePicker';
import { useKudosBadges } from '@/features/people/badges';
import { BottomSheet } from './BottomSheet';
import { primaryBtn } from './FeedCards';

export function KudosSheet({ onClose, to }: { onClose: (posted?: boolean) => void; to?: string }) {
  const ws = useWorkspaceId();
  const { userId } = useAuth();
  const toast = useUi(s => s.toast);
  const badges = useKudosBadges();
  const give = useGiveKudos(ws);
  const [people, setPeople] = useState<string[]>(to ? [to] : []);
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [picked, setPicked] = useState('');
  const badge = picked || badges.pickable[0]?.key || '';
  const [body, setBody] = useState('');
  const [photos, setPhotos] = useState<{ id: string; name: string; url: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const blocker = kudosBlocker({ body, people, teams: teamIds, uploading });
  const add = async (f: File) => {
    if (!ws) return;
    setUploading(true);
    try { const a = await uploadFile(f, { workspace_id: ws, purpose: 'kudos' }); setPhotos(p => [...p, { id: a.id, name: f.name, url: URL.createObjectURL(f) }]); }
    catch (e) { toast(uploadFailureText(errorMessage(e), "Couldn't upload photo"), 'error'); }
    finally { setUploading(false); }
  };
  const post = () => give.mutate({ body, badge: badge || undefined, recipient_ids: people, team_ids: teamIds, attachment_ids: photos.map(p => p.id) }, {
    onSuccess: () => { toast('Kudos posted 🎉', 'success'); onClose(true); },
    onError: e => toast(errorMessage(e), 'error'),
  });
  return (
    <BottomSheet open onClose={() => onClose(false)} title="Give kudos" testId="kudos-sheet">
      <div className="space-y-4 px-4 pb-3">
        <div>
          <p className="mb-1 text-sm font-medium">Who are you thanking?</p>
          <AudiencePicker value={{ people, teams: teamIds }} onChange={v => { setPeople(v.people.filter(id => id !== userId)); setTeamIds(v.teams); }} allow={(k, id) => k !== 'person' || id !== userId} label="Search people to thank"
            placeholder="Search people, departments and groups" helper={teamIds.length ? 'Everyone in a department or group you pick is credited.' : undefined} />
        </div>
        {badges.pickable.length > 0 && (
          <div>
            <p className="mb-1 text-sm font-medium">Badge</p>
            <ChipRow bleed height={48} className="gap-2" role="radiogroup" aria-label="Badge" data-testid="kudos-badges">
              {badges.pickable.map(b => (
                <button key={b.key} type="button" role="radio" aria-checked={badge === b.key} onClick={() => setPicked(b.key)} title={b.description ?? undefined}
                  className={clsx('flex h-11 shrink-0 items-center gap-1.5 rounded-full border px-3 text-sm', badge === b.key ? 'border-ink bg-ink text-white dark:border-white dark:bg-white dark:text-ink' : 'border-slate-200 dark:border-slate-700')}>
                  <span aria-hidden>{b.emoji}</span>{b.name}
                </button>
              ))}
            </ChipRow>
          </div>
        )}
        <label className="block">
          <span className="mb-1 block text-sm font-medium">Message</span>
          <textarea rows={4} value={body} onChange={e => setBody(e.target.value)} maxLength={5000} placeholder="What did they do that you appreciated?" data-testid="kudos-sheet-message"
            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-base focus:border-slate-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900" />
        </label>
        <div className="flex flex-wrap items-center gap-2">
          {photos.map(p => (
            <span key={p.id} className="relative h-16 w-16 overflow-hidden rounded-lg bg-slate-100 dark:bg-slate-800">
              <img src={p.url} alt={p.name} className="h-full w-full object-cover" />
              <button type="button" aria-label={`Remove ${p.name}`} onClick={() => setPhotos(x => x.filter(y => y.id !== p.id))} className="absolute right-0 top-0 grid h-7 w-7 place-items-center rounded-bl-lg bg-black/60 text-white"><X className="h-4 w-4" /></button>
            </span>
          ))}
          <button type="button" onClick={() => file.current?.click()} disabled={uploading || photos.length >= 10} data-testid="kudos-sheet-photo"
            className="flex h-16 items-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 text-sm font-medium text-slate-700 disabled:opacity-60 dark:border-slate-600 dark:text-slate-200">
            {uploading ? <Spinner className="h-4 w-4" /> : <ImagePlus className="h-5 w-5" aria-hidden />}Add photo
          </button>
          <input ref={file} type="file" accept="image/*" hidden onChange={e => { const f = e.target.files?.[0]; if (f) void add(f); e.target.value = ''; }} />
        </div>
      </div>
      <div className="sticky bottom-0 border-t border-slate-100 bg-white px-4 pt-3 dark:border-slate-800 dark:bg-slate-900">
        {blocker && <p className="mb-2 text-center text-xs text-slate-500" data-testid="kudos-sheet-blocker">{blocker}</p>}
        <button type="button" className={clsx(primaryBtn, 'w-full')} disabled={Boolean(blocker) || give.isPending} onClick={post} data-testid="kudos-sheet-post">
          {give.isPending && <Spinner className="h-4 w-4" />}Post kudos
        </button>
      </div>
    </BottomSheet>
  );
}
